<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1200"
    content-class="terms-dialog"
    overlay-opacity="0.99"
    :fullscreen="isMobile"
  >
    <v-card class="terms-dialog-card">
      <v-container class="pa-0">
        <v-row
          class="terms-row-background ma-0"
          justify="space-between"
          align="center"
        >
          <v-col class="pa-4">
            <div class="text-h5 pl-3">{{ $t('terms.termsOfUse') }}</div>
          </v-col>
          <v-col sm="1" md="1">
            <v-btn
              depressed
              @click="decline"
              rounded
              small
              color="lightGray"
              class="font-weight-bold close-btn mr-3"
              data-testid="terms-decline-btn"
            >
              <v-icon small>$close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row justify="space-between">
          <v-col md="3" class="pr-2" v-if="!isMobile">
            <v-card class="terms-nav ml-5 py-5" color="containerGray">
              <v-btn
                text
                block
                large
                class="terms-nav-btn justify-start"
                :class="{ active: currentTab == 0 }"
                @click.stop="switchDoc(0)"
                v-if="showTerms"
              >
                {{ $t('terms.vigilCloudTerms') }}
                <v-fade-transition hide-on-leave>
                  <v-icon small color="green" v-if="termsAgreed" class="pl-3"
                    >mdi-check</v-icon
                  >
                </v-fade-transition>
              </v-btn>
              <v-btn
                text
                block
                large
                class="terms-nav-btn justify-start"
                :class="{ active: currentTab == 1 }"
                @click.stop="switchDoc(1)"
                v-if="showAudio"
              >
                Audio Terms
                <v-fade-transition hide-on-leave>
                  <v-icon small color="green" v-if="audioAgreed" class="pl-3"
                    >mdi-check</v-icon
                  >
                </v-fade-transition>
              </v-btn>
            </v-card>
          </v-col>
          <v-col md="9" sm="9" class="pl-2">
            <v-card color="containerGray" class="text-card mr-5">
              <v-fade-transition hide-on-leave>
                <TermsComponent
                  ref="termsComponent"
                  @acceptTerms="setTermsAgreed"
                  :termsAgreed="termsAgreed"
                  :userClickOpen="userClickOpen"
                  v-if="currentTab == 0"
                />
              </v-fade-transition>
              <v-fade-transition hide-on-leave>
                <AudioComponent
                  @acceptTerms="setAudioAgreed"
                  :termsAgreed="audioAgreed"
                  :userClickOpen="userClickOpen"
                  v-if="currentTab == 1"
                />
              </v-fade-transition>
            </v-card>
          </v-col>
        </v-row>

        <v-row
          justify="space-between"
          class="terms-row-background ma-0"
          :class="{ 'terms-row-mobile': isMobile }"
          align="center"
        >
          <v-col class="ml-3" md="8" sm="12" cols="12" v-show="!userClickOpen">
            <div>
              {{ $t('terms.toContinueAcceptTerms') }}
            </div>
          </v-col>
          <v-col
            class="ma-0 mr-3"
            sm="12"
            md="3"
            cols="12"
            v-show="!userClickOpen"
          >
            <div class="d-flex justify-end pr-3">
              <v-btn
                depressed
                @click="decline"
                class="font-weight-bold"
                color="lightGray"
                >{{ $t('terms.declineAndLogout') }}</v-btn
              >
              <v-fade-transition hide-on-leave>
                <v-btn
                  class="ml-2 submit-btn font-weight-bold"
                  color="secondary"
                  depressed
                  :disabled="actionButtonDisabled"
                  @click="submitOrNext"
                  data-testid="accept-terms-btn"
                  >{{ actionButtonText }}</v-btn
                >
              </v-fade-transition>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>
<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import TermsComponent from './TermsComponent.vue'
import AudioComponent from './AudioComponent.vue'
import { EULA_VIEWED, EULA_ACCEPTED } from '@/constants/mixpanelEvents'
import { isMobile } from 'mobile-device-detect'

export default defineComponent({
  name: 'TermsDialog',
  components: {
    TermsComponent,
    AudioComponent,
  },
  data() {
    return {
      audioAgreed: false,
      currentTab: 0,
      dialog: false,
      showAudio: true,
      showTerms: true,
      termsAgreed: false,
      userClickOpen: false,
    }
  },
  computed: {
    ...mapState('Security', [
      'updateUserSuccess',
      'user',
      'isLoggedIn',
      'isTokenAuthenticated',
    ]),
    externalCloudCases() {
      return this.isTokenAuthenticated
    },
    actionButtonText() {
      if (this.termsAgreed && this.audioAgreed) {
        return this.$t('global.submit')
      } else if (this.currentTab == 1 && !this.audioAgreed) {
        return this.$t('global.submit')
      } else if (this.currentTab == 0 && !this.termsAgreed && !this.showAudio) {
        return this.$t('global.submit')
      } else {
        return this.$t('global.next')
      }
    },
    actionButtonDisabled() {
      if (this.currentTab == 0 && this.termsAgreed) {
        return false
      } else if (this.currentTab == 1 && this.audioAgreed) {
        return false
      } else {
        return true
      }
    },
    isMobile() {
      return isMobile
    },
  },
  methods: {
    ...mapActions('Security', ['acceptTerms', 'logout']),
    switchDoc(value) {
      this.currentTab = value
    },
    setTermsAgreed(value) {
      this.termsAgreed = value
    },
    setAudioAgreed(value) {
      this.audioAgreed = value
    },
    showDialog() {
      if (this.externalCloudCases) {
        return
      }
      this.currentTab = 3
      this.termsAgreed = false
      this.audioAgreed = false
      this.userClickOpen = false

      if (
        (!this.user.hasAcceptedTerms ||
          !this.user.hasAcceptedEndUserLicenseAgreement ||
          !this.user.hasAcceptedAudio) &&
        this.isLoggedIn
      ) {
        this.dialog = true
        this.setAcceptedDocs()
      }
    },
    setAcceptedDocs() {
      if (this.user.hasAcceptedTerms && !this.user.hasAcceptedAudio) {
        this.termsAgreed = true
        this.showTerms = false
        this.showAudio = true
        this.currentTab = 1
      } else if (!this.user.hasAcceptedTerms && this.user.hasAcceptedAudio) {
        this.audioAgreed = true
        this.showTerms = true
        this.showAudio = false
        this.currentTab = 0
      } else {
        this.showTerms = true
        this.showAudio = true
        this.currentTab = 0
        this.termsAgreed = false
        this.audioAgreed = false
      }
    },
    async decline() {
      this.dialog = false
      if (this.userClickOpen) {
        return
      }
      this.$mixpanelUserTrack(EULA_ACCEPTED, {
        EULA_ACCEPTED: false,
      })
      if (this.$refs.termsComponent) this.$refs.termsComponent.scrollTop()
      window.scrollTo(0, 0)
      await this.logout()
      this.$router.push('/login')
    },
    submitOrNext() {
      if (this.termsAgreed && this.audioAgreed) {
        this.submit()
      } else {
        this.currentTab = 1
      }
    },
    submit() {
      let payload = {
        UserId: this.user.userId,
        hasAcceptedTerms: this.termsAgreed,
        hasAcceptedEndUserLicenseAgreement: this.termsAgreed,
      }
      if (this.user.hasAcceptedTerms) {
        payload.hasAcceptedAudio = this.audioAgreed
      } else if (this.user.hasAcceptedAudio) {
        payload.hasAcceptedTerms = this.termsAgreed
      } else {
        payload.hasAcceptedTerms = this.termsAgreed
        payload.hasAcceptedAudio = this.audioAgreed
      }

      this.acceptTerms(payload).then(() => {
        if (this.updateUserSuccess) {
          this.dialog = false
          window.scrollTo(0, 0)
        }
      })
    },
    userClick() {
      this.$mixpanelUserTrack(EULA_VIEWED)
      this.dialog = true
      this.userClickOpen = true
      this.showTerms = true
      this.showAudio = true
      this.audioAgreed = false
      this.currentTab = 0
      this.termsAgreed = false
    },
  },
  mounted() {
    this.showDialog()
  },
  watch: {
    user() {
      this.showDialog()
    },
    isLoggedIn() {
      // this watch seems to resolve the odd terms dialog not showing issue
      this.showDialog()
    },
  },
})
</script>

<style lang="scss">
.terms-dialog {
  overflow: hidden;
}
.terms-dialog-card {
  border-radius: 2px;
  display: flex !important;
  flex-direction: column;
  .terms-row-background {
    background-color: $color-containerGray;
    height: 90px;
  }
  .terms-row-mobile {
    height: 100%;
  }
  .close-btn {
    float: right;
    width: 28px;
    border-radius: 50%;
  }
  .text-card {
    @media screen and (max-height: 815px) {
      max-height: 400px;
    }
    @media screen and (max-width: 500px) {
      max-height: 60vh;
    }
    max-height: 550px;
    overflow-y: auto;
  }
  .submit-btn {
    &.v-btn.v-btn--disabled:not(.v-btn--text):not(.v-btn--outlined) {
      background-color: rgba(226, 0, 57, 0.45) !important;
    }
  }
  .terms-nav {
    .terms-nav-btn {
      width: 100%;
      span {
      }
      &:hover {
        background-color: $color-hoverGray;
        border-radius: 0;
      }
      &.active {
        background-color: $color-hoverGray;
        border-radius: 0;
        font-weight: 800;
      }
    }
  }
}
</style>
